import React from 'react';
import { Bell, DollarSign, MessageCircle, Network, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useNotifications, Notification } from '@/hooks/useNotifications';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const NotificationBell = () => {
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead } = useNotifications();
  const navigate = useNavigate();

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      markAsRead(notification);
    }
    
    if (notification.type === 'payment' && notification.conversation_id) {
      navigate(`/conversations/${notification.conversation_id}`);
    } else if (notification.type === 'conversation' && notification.conversation_id) {
      navigate(`/conversations/${notification.conversation_id}`);
    } else if (notification.type === 'chat_room' && notification.room_id) {
      navigate(`/chat-rooms/${notification.room_id}`);
    }
  };

  const formatAmount = (cents: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  };

  const getNotificationIcon = (notification: Notification) => {
    switch (notification.type) {
      case 'payment':
        return (
          <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/30">
            <DollarSign className="h-4 w-4 text-green-600 dark:text-green-400" />
          </div>
        );
      case 'conversation':
        return (
          <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/30">
            <MessageCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </div>
        );
      case 'chat_room':
        return (
          <div className="p-2 rounded-full bg-purple-100 dark:bg-purple-900/30">
            <Network className="h-4 w-4 text-purple-600 dark:text-purple-400" />
          </div>
        );
      default:
        return (
          <div className="p-2 rounded-full bg-muted">
            <Bell className="h-4 w-4 text-muted-foreground" />
          </div>
        );
    }
  };

  const getNotificationTitle = (notification: Notification) => {
    switch (notification.type) {
      case 'payment':
        return notification.title;
      case 'conversation':
        return `Message from ${notification.sender_name}`;
      case 'chat_room':
        return notification.room_name || 'Chat Room';
      default:
        return notification.title;
    }
  };

  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <Badge 
                  className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-primary hover:bg-primary"
                >
                  {unreadCount > 9 ? '9+' : unreadCount}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>Notifications</p>
        </TooltipContent>
      </Tooltip>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h4 className="font-semibold">Notifications</h4>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={markAllAsRead}
              className="text-xs h-7"
            >
              <Check className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        
        <ScrollArea className="h-[300px]">
          {loading ? (
            <div className="p-4 text-center text-muted-foreground text-sm">
              Loading...
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No notifications yet</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`w-full p-4 text-left hover:bg-muted/50 transition-colors ${
                    !notification.is_read ? 'bg-primary/5' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {getNotificationIcon(notification)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm truncate">
                          {getNotificationTitle(notification)}
                        </p>
                        {!notification.is_read && (
                          <span className="h-2 w-2 rounded-full bg-primary flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {notification.message}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        {notification.type === 'payment' && notification.amount_cents && notification.currency && (
                          <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                            {formatAmount(notification.amount_cents, notification.currency)}
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
